


import {
  defineComponent,
  computed,
  PropType,
  ref,
} from '@vue/composition-api';

import type { StandardReportExt } from '@/models/externalReports/standard/standardReport';
import { dtFormat } from '@/lib/dateHelper';
import { useRoute } from '@/hooks/useRoute';
import { PaneStyle } from '@/models/externalReports/standard/standardMap';
import {
  PANE_SIDE_HARD_MAX_WIDTH_PX,
  LIST_HARD_MAX_HEIGHT_PX,
} from '@/components/ExternalReports/Standard/consts';
import { useStandardReports } from '@/components/ExternalReports/Standard/composables/useReports';
import type {
  ExternalReportSettings,
  ExternalReportsLabelPc,
} from '@/models/apis/externalReports/external_report_settings';

export default defineComponent({
  props: {
    userSettings: {
      type: Object as PropType<ExternalReportSettings>,
      required: true,
    },
    paneStyle: {
      type: Object as PropType<PaneStyle>,
      default: () => ({
        paneSideMinWidth: PANE_SIDE_HARD_MAX_WIDTH_PX,
        paneSideMaxWidth: PANE_SIDE_HARD_MAX_WIDTH_PX,
        listMinHeight: LIST_HARD_MAX_HEIGHT_PX,
        listMaxHeight: LIST_HARD_MAX_HEIGHT_PX,
      }),
    },
  },
  emits: ['click-report-on-report-list', 'change-photo-type-visibility', 'show-photo-modal'],
  setup(props, { emit }) {
    const { reportState, getSelectedReport } = useStandardReports();
    const photoTypes = computed<string[]>(() => props.userSettings.choices.photo_types);
    const checkedPhotoTypes = ref<Record<string, boolean>>(photoTypes.value.reduce((acc, type) => {
      acc[type] = true;
      return acc;
    }, {} as Record<string, boolean>));
    const pcLabels = computed<ExternalReportsLabelPc>(() => (props.userSettings?.pc ?? {}) as ExternalReportsLabelPc);

    const baseArrivalTsDisp = (report: StandardReportExt): string => {
      const baseDepartureDate = dtFormat(report.baseDepartureTs, 'yyyy/mm/dd');
      const baseArrivalDate = dtFormat(report.baseArrivalTs, 'yyyy/mm/dd');
      if (baseDepartureDate === baseArrivalDate) {
        return dtFormat(report.baseArrivalTs, 'HH:MM:SS');
      }
      return dtFormat(report.baseArrivalTs, 'yyyy/mm/dd HH:MM:SS');
    };
    const onClickReportOnReportList = (report: StandardReportExt) => {
      emit('click-report-on-report-list', report);
    };
    const onChangePhotoTypeVisibility = (photoType: string) => {
      emit('change-photo-type-visibility', photoType, checkedPhotoTypes.value[photoType]);
    };
    const { router } = useRoute();
    const onClickReportPrintBtn = (event: MouseEvent, standardReportId: number) => {
      // 一覧の印刷ボタンを押した時に、報告書の選択イベントが発生しないようにする
      event.stopPropagation();
      const routeObj = {
        name: 'PrintExternalReportsStandardReport',
        params: {
          externalReportId: standardReportId.toString(),
        },
      };
      const obj = router.resolve(routeObj);
      window.open(obj.href, '_blank');
    };
    const getPhotoCountByType = (photoType: string): number => {
      const selectedReport = getSelectedReport();
      if (!selectedReport) return 0;
      return selectedReport.photosExt.filter(photo => photo.photoType === photoType).length;
    };

    const showPhotosByType = (photoType: string) => {
      const selectedReport = getSelectedReport();
      if (!selectedReport) return;

      const photos = selectedReport.photosExt.filter(photo => photo.photoType === photoType);
      emit('show-photo-modal', photoType, photos);
    };
    return {
      // refs
      reportState,
      photoTypes,
      pcLabels,
      checkedPhotoTypes,
      // methods
      baseArrivalTsDisp,
      onClickReportOnReportList,
      onChangePhotoTypeVisibility,
      onClickReportPrintBtn,
      getPhotoCountByType,
      showPhotosByType,
      dtFormat,
    };
  },
});
